import { createApi, createCustomServiceCall } from '@thinknimble/tn-models'
import { axiosInstance } from '../axios-instance'
import { z } from 'zod'
import {
  memberShape,
  inviteMemberShape,
  updateMemberRoleShape,
  roleShape,
  createRoleShape,
  updateRoleShape,
} from './models'

const listMembers = createCustomServiceCall({
  inputShape: { orgId: z.string().uuid() },
  outputShape: z.array(z.object(memberShape)),
  cb: async ({ client, input, utils: { fromApi } }) => {
    const response = await client.get(`/memberships/?organization=${input.orgId}/`)
    return fromApi(response.data)
  },
})

const inviteMember = createCustomServiceCall({
  inputShape: {
    orgId: z.string().uuid(),
    ...inviteMemberShape,
  },
  outputShape: memberShape,
  cb: async ({ client, input, utils: { fromApi, toApi } }) => {
    const { orgId, ...data } = input
    const response = await client.post(
      '/memberships/invite/',
      toApi({ organization: orgId, ...data }),
    )
    return fromApi(response.data)
  },
})

const updateMemberRole = createCustomServiceCall({
  inputShape: {
    memberId: z.string().uuid(),
    ...updateMemberRoleShape,
  },
  outputShape: memberShape,
  cb: async ({ client, input, utils: { fromApi, toApi } }) => {
    const { memberId, ...data } = input
    const response = await client.patch(`/memberships/${memberId}/`, toApi(data))
    return fromApi(response.data)
  },
})

const removeMember = createCustomServiceCall({
  inputShape: {
    memberId: z.string().uuid(),
  },
  cb: async ({ client, input }) => {
    await client.delete(`/memberships/${input.memberId}/`)
  },
})

const listRoles = createCustomServiceCall({
  outputShape: z.array(z.object(roleShape)),
  cb: async ({ client, utils: { fromApi } }) => {
    const response = await client.get('/roles/')
    const results = response.data.results ?? response.data
    return fromApi(results)
  },
})

const createRole = createCustomServiceCall({
  inputShape: createRoleShape,
  outputShape: roleShape,
  cb: async ({ client, input, utils: { fromApi, toApi } }) => {
    const response = await client.post('/roles/', toApi(input))
    return fromApi(response.data)
  },
})

const updateRole = createCustomServiceCall({
  inputShape: {
    id: z.string().uuid(),
    ...updateRoleShape,
  },
  outputShape: roleShape,
  cb: async ({ client, input, utils: { fromApi, toApi } }) => {
    const { id, ...data } = input
    const response = await client.patch(`/roles/${id}/`, toApi(data))
    return fromApi(response.data)
  },
})

const deleteRole = createCustomServiceCall({
  inputShape: z.string().uuid(),
  cb: async ({ client, input }) => {
    await client.delete(`/roles/${input}/`)
  },
})

export const memberApi = createApi({
  client: axiosInstance,
  baseUri: '/memberships/',
  models: {
    entity: memberShape,
  },
  customCalls: {
    listMembers,
    inviteMember,
    updateMemberRole,
    removeMember,
    listRoles,
    createRole,
    updateRole,
    deleteRole,
  },
})
